import React, { useCallback, useEffect, useState } from "react";
import type z from "zod";
import {
  faDownload,
  faUpload,
  faFileZipper,
  faUserTag,
  faUserXmark,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useAuthorization } from "../authorization/useAuthorization";
import { api } from "../utils/api";
import { schemas } from "../api/generated";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Spinner, Table } from "react-bootstrap";
import { useDownload } from "../utils/useDownload";
import { humanFileSize } from "../utils/helper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ConfirmationModal } from "../components/modals/ConfirmationModal";
import { AuthorizationErrorMessage } from "../authorization/AuthorizationErrorMessage";
import { buildUserCountText } from "../utils/buildUserCountText";
import { DeletionModal } from "../components/modals/DeletionModal";
import { InlineEdit } from "../components/InlineEdit";
import {
  getUnassignFileCustomErrorMessage,
  getUpdateFileCustomErrorMessage,
} from "../utils/errorMessages";
import { ReplaceAttackLibraryModal } from "../components/modals/ReplaceAttackLibraryModal";

type FileDTO = z.infer<typeof schemas.FileWithUsage>;
type UserDTO = z.infer<typeof schemas.User>;
const ProblemDetails = schemas.ProblemDetails;

export default function FilePage() {
  const { fileName } = useParams<{ fileName: string }>();
  const auth = useAuthorization({ userTypes: ["Admin"] });
  const [file, setFile] = useState<FileDTO | undefined>();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const { downloadFile, isDownloading, downloadError } = useDownload();
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [showReplacementModal, setShowReplacementModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.authorized) {
      // TODO: For now we fetch all files and then find the right one. Later we should add an endpoint to get a file by ID or name.
      api
        .getFiles(auth.token, 1000, 0, "createdAt_desc", fileName)
        .then((files) => {
          const foundFile = files.data.find(
            (f) => f.id === fileName || f.fileName === fileName
          );
          setFile(foundFile);
        })
        .catch((error) => {
          console.warn("Failed to fetch files.", error);
          setErrorMessage("Failed to load file details.");
          setFile(undefined);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [auth, fileName]);

  const handleDownload = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!file || !auth.authorized || isDownloading) {
        return;
      }
      downloadFile(
        `/api/file-download/${file.fileName}`,
        file.fileName,
        auth.token
      );
    },
    [downloadFile, file, isDownloading, auth]
  );

  const handleFileNameChangeRequest = useCallback(
    async (newFileName: string) => {
      if (!file || !auth.authorized) return;

      const originalFileName = file.fileName;

      // Optimistically update the UI
      setFile((prevFile) =>
        prevFile ? { ...prevFile, fileName: newFileName } : undefined
      );

      try {
        // Send the update request
        const updatedFile = await api.updateFile(auth.token, file.id!, {
          fileName: newFileName,
        });

        navigate(`/files/${updatedFile.fileName}`, {
          replace: true,
        });
        toast.success("File name updated successfully");
      } catch (error) {
        setFile((prevFile) =>
          prevFile ? { ...prevFile, fileName: originalFileName } : undefined
        ); // Revert optimistic update
        const message = await ProblemDetails.parseAsync(error)
          .then(getUpdateFileCustomErrorMessage)
          .catch(() => "Failed to update file name");
        toast.error(message);
      }
    },
    [auth, file, navigate]
  );

  if (!auth.authorized) {
    return <AuthorizationErrorMessage {...auth} />;
  }

  if (isLoading) {
    return (
      <div className="App my-md-3 my-1 container">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "40vh" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  return (
    <div className="App my-md-3 my-1 container">
      <div style={{ flex: "auto" }}>
        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        )}
        {file ? (
          <>
            <div className="row">
              <h2>
                <FontAwesomeIcon icon={faFileZipper} className="me-3" />
                File Details
              </h2>
              <hr className="mb-lg-4" />
            </div>
            <div className="row gx-5">
              <div className="col-xl-5 col-lg-6 mb-4 mb-lg-0">
                <FileDetails
                  file={file}
                  onChangeFileNameRequest={handleFileNameChangeRequest}
                />
                <div className="d-flex gap-2 mt-3 ">
                  {/* <ButtonGroup aria-label="Basic example"> */}
                  <a
                    href={`/api/file-download/${file.fileName}`}
                    className="btn btn-dark btn-sm"
                    role="button"
                    onClick={handleDownload}
                  >
                    {isDownloading ? (
                      "Downloading..."
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={faDownload}
                          // className="me-2"
                          aria-hidden="true"
                        />{" "}
                        Download
                      </>
                    )}
                  </a>
                  <Button
                    variant="dark"
                    // className="mx-2"
                    size="sm"
                    onClick={() => setShowReplacementModal(true)}
                  >
                    <FontAwesomeIcon icon={faUpload} aria-hidden="true" />{" "}
                    Replace
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    // className="mx-2"
                    onClick={() => setShowDeletionModal(true)}
                  >
                    <FontAwesomeIcon
                      icon={faTrash}
                      // className="me-2"
                      aria-hidden="true"
                    />{" "}
                    Delete
                  </Button>
                  {downloadError && (
                    <p className="alert alert-danger">
                      {downloadError.message}
                    </p>
                  )}
                  {/* </ButtonGroup> */}
                </div>
              </div>
              <div className="col-xl-7 col-lg-6">
                <FileAssignments file={file} />
              </div>
            </div>
          </>
        ) : (
          <p>{fileName ? "File not found." : "No file selected."}</p>
        )}
      </div>

      <DeletionModal
        showDeletionModal={showDeletionModal}
        setShowDeletionModal={setShowDeletionModal}
        token={auth.token}
        selectedFile={file}
        onDeleted={async () => {
          setIsLoading(true);
          navigate("/admin-home/files");
        }}
      />

      {file && (
        <ReplaceAttackLibraryModal
          originalFile={file}
          show={showReplacementModal}
          setShow={setShowReplacementModal}
          token={auth.token}
          onFileReplaced={(file) => {
            // Refresh file details
            // setFile(file);
            if (file.fileName !== fileName) {
              navigate(`/files/${file.fileName}`, { replace: true });
            }
          }}
        />
      )}
    </div>
  );
}

function FileDetails({
  file,
  onChangeFileNameRequest,
}: {
  file: FileDTO;
  // eslint-disable-next-line no-unused-vars
  onChangeFileNameRequest?: (fileName: string) => Promise<void>;
}) {
  return (
    <dl>
      <dt id="file-name-label">File Name</dt>
      <dd className="font-monospace">
        <InlineEdit
          value={file.fileName}
          onChangeCommit={async (value) => {
            await onChangeFileNameRequest?.(value);
          }}
          validate={(value) => {
            if (!/^[a-z0-9._-]+$/.test(value))
              return "File name can only contain a-z, 0-9, ., _, and -";
            else if (!value.endsWith(".7z"))
              return "File name must end with .7z";
            else return true;
          }}
          name="file-name"
          aria-labelledby="file-name-label"
          onInput={(e) => {
            // force lowercase
            const target = e.target as HTMLInputElement;
            target.value = target.value.toLowerCase();
          }}
          maxLength={255}
        />
      </dd>
      <dt>Created</dt>
      <dd title={file.createdAt}>
        {new Date(file.createdAt).toLocaleString()}
      </dd>
      <dt>Modified</dt>
      <dd title={file.updatedAt}>
        {new Date(file.updatedAt).toLocaleString()}
      </dd>
      <dt>Size</dt>
      <dd title={file.fileSize.toLocaleString() + " bytes"}>
        {humanFileSize(file.fileSize)}
      </dd>
      <dt>File Type</dt>
      <dd className="font-monospace">{file.fileType}</dd>
      <dt>Assignments</dt>
      <dd>
        {buildUserCountText({ count: file.assignmentCount, noneText: "none" })}
      </dd>
    </dl>
  );
}

/* Component for displaying users who have the file assigned to them and allow adding/removing assignments */
function FileAssignments({ file }: { file: FileDTO }) {
  const [assignedUsers, setAssignedUsers] = useState<UserDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const auth = useAuthorization({ userTypes: ["Admin"] });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserDTO | null>(null);

  const fetchAssignedUsers = useCallback(() => {
    if (!auth.authorized) return Promise.resolve();
    setIsLoading(true);
    return api
      .getUsersByFileAssignment(auth.token, file.fileName)
      .then((users) => {
        setAssignedUsers(users || []);
      })
      .catch((error) => {
        console.warn("Failed to fetch assigned users.", error);
        toast.error("Failed to fetch assigned users.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [auth, file.fileName]);

  const handleUnassign = useCallback(
    async (user: UserDTO) => {
      if (!auth.authorized) {
        return Promise.resolve();
      }

      setIsLoading(true);
      setShowConfirmation(false);
      try {
        await api.unassignFileFromUser(auth.token, file.id!, user.id);

        toast.success(`Successfully unassigned file from user.`);

        await fetchAssignedUsers();
      } catch (error) {
        const message = await ProblemDetails.parseAsync(error)
          .then(getUnassignFileCustomErrorMessage)
          .catch(() => "Failed to unassign file from user");
        toast.error(message);
      }
    },
    [auth, file.id, fetchAssignedUsers]
  );

  useEffect(() => {
    setTimeout(() => {
      fetchAssignedUsers();
    }, 0);
  }, [fetchAssignedUsers]);

  if (!auth.authorized) {
    return null;
  }

  return (
    <div>
      <h3>
        <FontAwesomeIcon icon={faUserTag} className="me-2" /> User Assignments
      </h3>
      {isLoading ? (
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      ) : assignedUsers.length > 0 ? (
        <AssignedUsersTable
          users={assignedUsers}
          onUnassign={async (user) => {
            setSelectedUser(user);
            setShowConfirmation(true);
          }}
        />
      ) : (
        <p className="text-muted">
          No users currently have this file assigned.
        </p>
      )}
      <ConfirmationModal
        title="Confirmation"
        prompt="Are you sure you want to unassign this file from the user?"
        confirmButtonText="Unassign"
        confirmButtonEnabled={!!file.id}
        show={showConfirmation}
        onShow={setShowConfirmation}
        onConfirmed={() => handleUnassign(selectedUser!)}
      ></ConfirmationModal>
    </div>
  );
}

/* Displays a table of users with unassign buttons */
function AssignedUsersTable({
  users,
  onUnassign,
}: {
  users: UserDTO[];
  // eslint-disable-next-line no-unused-vars
  onUnassign: (user: UserDTO) => void;
}) {
  return (
    <Table>
      <thead>
        <tr>
          <th>Name</th>
          <th>Username</th>
          <th className="text-center">Action</th>
        </tr>
      </thead>
      <tbody>
        {users
          .sort((a, b) => a.username.localeCompare(b.username))
          .map((user) => (
            <tr key={user.id}>
              <td
                title={[user.firstname, user.middlename, user.lastname]
                  .filter((n) => !!n)
                  .join(" ")}
              >
                {[
                  user.firstname,
                  user.middlename ? user.middlename?.slice(0, 1) + "." : "",
                  user.lastname,
                ].join(" ")}
              </td>
              <td className="text-truncate">{user.username}</td>
              <td className="text-center">
                <Button
                  variant="outline-dark"
                  size="sm"
                  title="Unassign from user"
                  aria-label="Unassign from user"
                  onClick={() => {
                    onUnassign(user);
                  }}
                >
                  <FontAwesomeIcon width={20} icon={faUserXmark} />
                </Button>
              </td>
            </tr>
          ))}
      </tbody>
    </Table>
  );
}
